import {
  programmeOverviewQuery,
  programmesQuery,
} from "@common/api/maintenance-programme"
import { Button } from "@common/components/ui/button"
import { useMaxWidth } from "@common/hooks/use-mobile"
import { useHasPermission } from "@common/hooks/use-permissions"
import {
  createFileRoute,
  Link,
  Outlet,
  useMatches,
  useNavigate,
} from "@tanstack/react-router"
import { useState } from "react"
import { ActiveFilterBar } from "@/components/layout/active-filter-bar"
import { ColumnChips } from "@/components/layout/column-chips"
import { DETAIL_SHEET_BREAKPOINT } from "@/components/layout/detail-panel"
import { DisplayOptions } from "@/components/layout/display-options"
import { FilterButton } from "@/components/layout/filter-button"
import { ListPage } from "@/components/layout/list-page"
import { ListTabs } from "@/components/layout/list-tabs"
import { ListToolbar } from "@/components/layout/list-toolbar"
import { NameFilter } from "@/components/layout/name-filter"
import { useColumnSizing } from "@/hooks/use-column-layout"
import { useWidth } from "@/hooks/use-width"
import { fitColumns } from "@/lib/column-widths"
import { ProgrammeOverview } from "./-components/overview/programme-overview"
import { type Picked, ProgrammeTable } from "./-components/programme-table"
import {
  COLUMN_LABELS,
  COLUMN_SIZING,
  COLUMNS,
  type Column,
  DEFAULT_COLUMNS,
} from "./-lib/columns"
import {
  hasProgrammeFilters,
  NO_PROGRAMME_FILTERS,
  type ProgrammeFilterValues,
  programmeFilterConfigs,
} from "./-lib/filters"
import { requireProgrammePages } from "./-lib/gate"
import { GROUPING_LABELS, GROUPINGS } from "./-lib/grouping"
import {
  displayOf,
  PROGRAMMES,
  type ProgrammesDisplay,
  type ProgrammesSearch,
  programmesSearchSchema,
  TABS,
  tabLabel,
} from "./-lib/search"

export const Route = createFileRoute("/_layout/maintenance-programmes")({
  staticData: { breadcrumb: "Maintenance programs" },
  validateSearch: (search) => programmesSearchSchema.parse(search),
  loaderDeps: ({ search: { tab } }) => ({ overview: tab === "overview" }),
  beforeLoad: ({ context: { queryClient } }) =>
    requireProgrammePages(queryClient),
  loader: ({ context: { queryClient }, deps }) =>
    deps.overview
      ? queryClient.ensureQueryData(programmeOverviewQuery())
      : queryClient.ensureQueryData(programmesQuery(PROGRAMMES)),
  component: ProgrammesPage,
})

function useUpdateSearch(): (patch: Partial<ProgrammesSearch>) => void {
  const navigate = useNavigate()
  return (patch) =>
    navigate({
      to: ".",
      search: (previous) => ({ ...previous, ...patch }),
      replace: true,
    })
}

function ProgrammesPage(): React.ReactElement {
  const search = Route.useSearch()
  const display = displayOf(search)
  const [ref, width] = useWidth<HTMLDivElement>()
  const sizing = useColumnSizing(display.columns, COLUMN_SIZING)
  const fit = fitColumns(display.columns, width, sizing)
  const [picked, setPicked] = useState<Picked>(new Set())
  const open = useMatches({
    select: (matches) =>
      matches.some((match) => match.staticData.detailSidebar),
  })
  const compact = useMaxWidth(DETAIL_SHEET_BREAKPOINT)

  return (
    <ListPage
      toolbar={<Toolbar display={display} hidden={fit.hidden} />}
      pane={open && !compact ? "split" : "closed"}
      detail={<Outlet />}
      scrollRef={ref}
    >
      {search.tab === "overview" ? (
        <ProgrammeOverview />
      ) : (
        <ProgrammeList
          display={display}
          columns={fit.shown}
          picked={picked}
          onPick={setPicked}
        />
      )}
    </ListPage>
  )
}

function ProgrammeList({
  display,
  columns,
  picked,
  onPick,
}: {
  display: ProgrammesDisplay
  columns: Column[]
  picked: Picked
  onPick: (next: Picked) => void
}): React.ReactElement {
  const search = Route.useSearch()
  const update = useUpdateSearch()

  return (
    <>
      <ActiveFilters />
      <ProgrammeTable
        search={search}
        display={display}
        columns={columns}
        picked={picked}
        onPick={onPick}
        onSort={(column) => update(sortSearch(display, column))}
        onClear={() => update({ ...NO_PROGRAMME_FILTERS, search: undefined })}
      />
    </>
  )
}

function Toolbar({
  display,
  hidden,
}: {
  display: ProgrammesDisplay
  hidden: Column[]
}): React.ReactElement {
  const { tab = "all" } = Route.useSearch()
  const update = useUpdateSearch()
  const canCreate = useHasPermission("maintenance_programme:create")

  return (
    <ListToolbar
      tabs={
        <ListTabs
          tabs={TABS}
          tab={tab}
          label={tabLabel}
          onSelect={(next) =>
            update({ tab: next === "all" ? undefined : next })
          }
        />
      }
      actions={
        <>
          {tab !== "overview" && (
            <ListActions display={display} hidden={hidden} />
          )}
          {canCreate && (
            <Button asChild size="sm">
              <Link to="/maintenance-programmes/new" search={true}>
                New program
              </Link>
            </Button>
          )}
        </>
      }
    />
  )
}

function ListActions({
  display,
  hidden,
}: {
  display: ProgrammesDisplay
  hidden: Column[]
}): React.ReactElement {
  const values = Route.useSearch()
  const { search = "" } = values
  const update = useUpdateSearch()

  return (
    <>
      <NameFilter
        search={search}
        onSearch={(next) => update({ search: next || undefined })}
        placeholder="Search programs…"
      />
      <FilterButton
        active={hasProgrammeFilters(values)}
        configs={programmeFilterConfigs}
        onChange={update}
      />
      <DisplayOptions
        groupings={GROUPINGS}
        labels={GROUPING_LABELS}
        group={display.group}
        subgroup={undefined}
        onChange={(next) => update({ group: next.group })}
      >
        <ColumnChips
          options={COLUMNS}
          labels={COLUMN_LABELS}
          fixed={["number"]}
          columns={display.columns}
          hidden={hidden}
          onChange={(next) => update(columnsSearch(next))}
        />
      </DisplayOptions>
    </>
  )
}

function columnsSearch(columns: Column[]): Partial<ProgrammesSearch> {
  const defaults =
    columns.length === DEFAULT_COLUMNS.length &&
    columns.every((column) => DEFAULT_COLUMNS.includes(column))
  return { columns: defaults ? undefined : columns }
}

function sortSearch(
  display: ProgrammesDisplay,
  column: Column,
): Partial<ProgrammesSearch> {
  const direction =
    column === display.sort && display.direction === "asc" ? "desc" : "asc"
  const byDefault = column === "title" && direction === "asc"
  return byDefault
    ? { sort: undefined, direction: undefined }
    : { sort: column, direction }
}

function ActiveFilters(): React.ReactElement {
  const { manufacturer, model, updater } = Route.useSearch()
  const update = useUpdateSearch()
  const values: ProgrammeFilterValues = { manufacturer, model, updater }

  return (
    <ActiveFilterBar
      active={hasProgrammeFilters(values)}
      configs={programmeFilterConfigs}
      values={values}
      onChange={update}
      onClear={() => update(NO_PROGRAMME_FILTERS)}
    />
  )
}
